import { styled, alpha } from '@mui/material/styles';
import { Typography, Chip, Stack } from '@mui/material';

// Grid Container
interface CardsGridProps {
  $gap?: string;
  /** Columns currently fitting a row; pins every card to that exact width so
   *  a shorter trailing row doesn't stretch its cards wider than the rest */
  $columns?: number;
}

export const CardsGrid = styled('div')<CardsGridProps>`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ $gap }) => $gap || '20px'};
  width: 100%;

  ${({ $columns, $gap }) => {
    if (!$columns || $columns < 1) return '';
    const width = `calc((100% - ${$columns - 1} * ${$gap || '20px'}) / ${$columns})`;
    return `
      > * {
        flex: 0 0 ${width};
        width: ${width};
      }
    `;
  }}
`;

// Card Container
interface CardContainerProps {
  $width?: string;
  $clickable?: boolean;
}

export const CardContainer = styled('div')<CardContainerProps>`
  position: relative;
  z-index: 1;
  padding: 20px;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  /* Fallback sizing for when the parent CardsGrid isn't pinning an exact
     per-column width (see CardsGrid's $columns rule, which overrides this).
     Grows to fill leftover row space, but never shrinks below its width. */
  flex: 1 1 ${({ $width }) => $width || '320px'};
  min-width: min(${({ $width }) => $width || '320px'}, 100%);
  max-width: 100%;
  min-height: 180px;
  box-sizing: border-box;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: all 0.3s ease;

  backdrop-filter: ${({ theme }) =>
    theme.palette.mode === 'dark' ? 'blur(20px)' : 'blur(14px) saturate(120%)'};
  -webkit-backdrop-filter: ${({ theme }) =>
    theme.palette.mode === 'dark' ? 'blur(20px)' : 'blur(14px) saturate(120%)'};

  background: ${({ theme }) =>
    theme.palette.mode === 'dark'
      ? `linear-gradient(145deg, ${alpha(theme.palette.grey['02'] || '', 0.85)} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`
      : alpha(theme.palette.fixed.white, 0.7)};

  border: 1px solid
    ${({ theme }) =>
      theme.palette.mode === 'dark'
        ? theme.palette.transparent.tertiary
        : theme.palette.transparent.quaternary};

  box-shadow: ${({ theme }) =>
    theme.palette.mode === 'dark'
      ? `0 4px 20px ${alpha(theme.palette.fixed.black, 0.4)}`
      : `0 2px 8px ${alpha(theme.palette.fixed.black, 0.04)}, 0 1px 3px ${alpha(theme.palette.fixed.black, 0.02)}`};

  ${({ $clickable, theme }) =>
    $clickable &&
    `
    &:hover {
      transform: translateY(-2px);
      box-shadow: ${
        theme.palette.mode === 'dark'
          ? `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}, 0 4px 16px ${alpha(theme.palette.fixed.black, 0.5)}`
          : `0 8px 24px ${alpha(theme.palette.primary.main, 0.1)}, 0 2px 8px ${alpha(theme.palette.fixed.black, 0.06)}`
      };
      border-color: ${
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.primary.main, 0.25)
          : alpha(theme.palette.primary.main, 0.2)
      };
    }
  `}
`;

// Card Header
export const CardHeader = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

export const CardChips = styled('div')`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

// Status Chip
interface StatusChipProps {
  $bgColor?: string;
  $textColor?: string;
}

export const StatusChip = styled(Chip)<StatusChipProps>`
  border-radius: 6px;
  height: 24px;
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 0.04em;
  background-color: ${({ $bgColor }) => $bgColor || '#949494'};
  color: ${({ $textColor }) => $textColor || '#ffffff'};
`;

// Info Rows
export const InfoRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  padding: 4px 0;

  .info-icon {
    color: ${({ theme }) => theme.palette.white['06']};
    flex-shrink: 0;
    opacity: 0.7;
  }

  .info-content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
  }
`;

export const InfoLabel = styled(Typography)`
  font-size: 11px;
  color: ${({ theme }) => theme.palette.grey['01']};
  font-weight: 600;
  opacity: 0.7;
  line-height: 1;
  text-align: left;
`;

export const InfoValue = styled(Typography)`
  font-size: 13px;
  font-weight: 500;
  line-height: 1.2;
  word-break: break-all;
  text-align: left;
`;

export const InfoFieldsStack = styled(Stack)`
  gap: 4px;
  margin-top: 4px;
`;
